import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  changerMotDePasse,
  demanderChangementAdresse,
  fermerLesConnexions,
  lireLesAdressesPassees,
  lireLesConnexions,
  lireLesRattachements,
  lireLesReglages,
  poserUnePreference,
  remettreLesExceptions,
} from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LES RÉGLAGES — `CDC-4` F-51 ET F-52, ET L'ÉCRAN EXISTE POUR LES QUATRE RÔLES.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **L'écran n'est pas le même pour tous, et ce n'est pas le front qui en décide** (Q481) :
 * « pour un tuteur et un élève il porte tout ce qui suit ; **pour les deux rôles qui
 * administrent, il porte l'adresse, le mot de passe et les connexions en cours, et rien
 * d'autre** — ils n'ont ni cours, ni espace de fichiers, ni conversation, donc les trois
 * préférences n'auraient aucun objet ».
 *
 * **Le serveur rend donc une union discriminée par `etendue`**, `complete` ou `reduite`, et
 * **le compilateur refuse une page qui lirait les préférences sans avoir testé le
 * discriminant**. C'est le contraire d'un test de rôle côté front : le front ne demande
 * jamais « qui es-tu », il lit ce que le serveur a décidé de lui donner.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE FEATURE NE FAIT PAS, ET CHAQUE ABSENCE EST UNE RÈGLE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun retrait d'adresse passée.** « Elle n'en retire aucune : le produit les garde
 *   pour **continuer à la reconnaître** quand une structure la provisionne avec l'adresse
 *   qu'elle connaît. En retirer une casserait cette reconnaissance en silence. » La route
 *   n'existe pas, et il n'y a donc rien à appeler.
 * - **Aucune fermeture de connexion ligne par ligne** (F-52) : « distinguer une connexion
 *   étrangère d'une des siennes sur une adresse réseau et un nom d'appareil **n'est pas
 *   quelque chose qu'une personne sait faire** ; un geste unique est **actionnable sans
 *   interprétation** ».
 * - **Aucun réglage d'exception de notification.** « L'exception se règle **DANS la
 *   conversation**, pas ici. L'écran en porte seulement le nombre et **un geste unique pour
 *   toutes les remettre** — sans quoi un fil coupé six mois plus tôt ne se retrouve plus.
 *   **Lister les conversations ici referait la surface de l'annuaire une seconde fois.** »
 * - **Aucun réglage de langue** (Q494) : « français seul ». Il n'y a pas de colonne.
 */

export function useReglages() {
  return useQuery({
    queryKey: CLES.reglages.ecran,
    queryFn: () => deballer(lireLesReglages()),
  });
}

export function useRattachements() {
  return useQuery({
    queryKey: CLES.reglages.rattachements,
    queryFn: () => deballer(lireLesRattachements()),
  });
}

export function useAdressesPassees() {
  return useQuery({
    queryKey: CLES.reglages.adressesPassees,
    queryFn: () => deballer(lireLesAdressesPassees()),
  });
}

export function useConnexions() {
  return useQuery({
    queryKey: CLES.reglages.connexions,
    queryFn: () => deballer(lireLesConnexions()),
    // **Rien ici n'est conservé pour être comparé** : le produit ne garde aucun « vu le ».
    // La liste se relit donc à chaque venue plutôt que de vieillir en silence.
    staleTime: 0,
  });
}

/**
 * **Poser une des trois préférences.**
 *
 * **Le nom de la préférence est dans le chemin, pas dans le corps**, et les trois se
 * posent par la même route. Le corps ne porte que les champs de la préférence visée : le
 * contrat les rend tous facultatifs parce qu'`media` en porte deux et qu'on peut n'en
 * changer qu'un.
 */
export function usePoserUnePreference() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (demande: {
      nom: 'media' | 'rangement' | 'notification';
      valeur: {
        micro?: boolean;
        camera?: boolean;
        dossierId?: string | null;
        emailDeConversation?: boolean;
      };
    }) =>
      deballer(
        poserUnePreference({
          path: { nom: demande.nom },
          body: demande.valeur,
        }),
      ),
    // **La réponse rend les trois préférences**, pas seulement celle qui a changé : le
    // cache se remplace donc au lieu de se recharger, et l'écran ne clignote pas.
    onSuccess: () => cache.invalidateQueries({ queryKey: CLES.reglages.tout }),
  });
}

export function useRemettreLesExceptions() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => deballer(remettreLesExceptions()),
    onSuccess: () => cache.invalidateQueries({ queryKey: CLES.reglages.tout }),
  });
}

export function useChangerLeMotDePasse() {
  return useMutation({
    mutationFn: (corps: {
      motDePasseCourant: string;
      nouveauMotDePasse: string;
    }) => deballer(changerMotDePasse({ body: corps })),
  });
}

/**
 * **Demander un changement d'adresse — la PREMIÈRE des deux étapes.**
 *
 * « Deux choses, **dans cet ordre** : le mot de passe courant, **puis** un code envoyé à
 * la nouvelle adresse. **Tant que le code n'est pas saisi, l'adresse du compte ne change
 * pas** » (Q492). Cette mutation ne fait donc que **demander** : elle ne change rien, et
 * l'écran doit le dire, sans quoi une personne croira que c'est fait.
 *
 * **La notification part à l'ANCIENNE adresse dès la demande**, pour que le titulaire
 * apprenne une **tentative** ; la confirmation part à la nouvelle **quand le changement
 * prend effet**. C'est le serveur qui envoie les deux, à leur moment.
 */
export function useDemanderLAdresse() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (corps: { motDePasseCourant: string; nouvelleAdresse: string }) =>
      deballer(demanderChangementAdresse({ body: corps })),
    onSuccess: () => cache.invalidateQueries({ queryKey: CLES.reglages.tout }),
  });
}

/**
 * **Fermer toutes ses connexions sauf celle d'où l'on agit** — F-52.
 *
 * « **C'est le seul levier du produit contre une session reprise**, risque que le corpus
 * avait accepté en écrivant que rien ne signale la reprise et qu'aucune trace n'en est
 * gardée. **Une session vaut sept jours : sans ce geste, la reprise durait sept jours quoi
 * qu'il arrive.** »
 *
 * **Le mot de passe courant est exigé**, et le refus sans lui porte `MOT_DE_PASSE_INVALIDE`.
 */
export function useFermerLesConnexions() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (corps: { motDePasseCourant: string }) =>
      deballer(fermerLesConnexions({ body: corps })),
    onSuccess: () =>
      cache.invalidateQueries({ queryKey: CLES.reglages.connexions }),
  });
}
